import React, { forwardRef, useId, useImperativeHandle } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Stack, Text, InputText } from "@gusarov-studio/rubik-ui";
import { NewPromptSchema, type NewPrompt } from "./NewPromptFormSchema";

interface NewPromptFormProps {
  disabled: boolean;
  defaultValues: NewPrompt;
  onSubmit: (data: NewPrompt) => void;
  id?: string;
}

interface NewPromptFormRef {
  submit: () => void;
  reset: () => void;
}

const NewPromptForm = forwardRef<NewPromptFormRef, NewPromptFormProps>((props, ref) => {
  const { defaultValues, disabled, onSubmit, id } = props;

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset: resetForm,
  } = useForm<NewPrompt>({
    defaultValues: defaultValues,
    resolver: zodResolver(NewPromptSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const newId = useId();
  const formId = id ?? newId;
  const nameFieldId = useId();
  const submitForm = handleSubmit(onSubmit);

  // Expose instance API
  useImperativeHandle(ref, () => ({
    submit: submitForm,
    reset: resetForm,
  }));

  return (
    <form
      id={formId}
      onSubmit={submitForm}
    >
      <Stack spacing={6}>
        <Text
          as="label"
          htmlFor={nameFieldId}
          weight="500"
          size="14"
          color="primary"
        >
          Prompt name
        </Text>

        <InputText
          id={nameFieldId}
          autoFocus
          invalid={Boolean(errors.name)}
          disabled={disabled}
          {...register("name")}
        />

        {errors.name?.message && (
          <Text
            color="danger"
            size="14"
          >
            {errors.name?.message}
          </Text>
        )}
      </Stack>
    </form>
  );
});

NewPromptForm.displayName = "NewPromptForm";

export { NewPromptForm, type NewPromptFormRef };
